import { useState, useEffect } from "react";
import {
  Button,
  Box,
  TextField,
  InputAdornment,
  Typography,
  Divider,
  Dialog,
  DialogActions,
  DialogTitle,
  useMediaQuery,
} from "@mui/material";
import { useTheme, styled } from "@mui/material/styles";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import DeleteIcon from "@mui/icons-material/Delete";

import { useDispatch } from "react-redux";
import {
  addEmpAdmin,
  updateEmpAdmin,
  removeEmpAdmin,
} from "../store/empAdminSlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ApiEmpAdmin, endpoints_empAdmin } from "src/app/api/EmpAdminApi";

const api = new ApiEmpAdmin();

const StyledBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(3),
  backgroundColor: "#ffffff",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(4),
  flexWrap: "wrap",
}));

function EmpAdminForm({ isEdit, empAdmin, handleClose }) {
  const [openDelete, setOpenDelete] = useState(false);
  const dispatch = useDispatch();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const schema = yup.object().shape({
    name: yup.string().trim().required("Nombre es requerido"),
    active: yup.boolean().optional(),
  });

  const notifyAddOk = () =>
    toast.success("Empresa creada con éxito", { position: "top-center", autoClose: 1000 });
  const notifyAddError = () =>
    toast.error("No se pudo crear la empresa", { position: "top-center", autoClose: 1000 });
  const notifyUpdtateOk = () =>
    toast.success("Actualización exitosa", { position: "top-center", autoClose: 1000 });
  const notifyUpdtateError = () =>
    toast.error("Error al intentar actualizar la empresa", { position: "top-center", autoClose: 1000 });
  const notifyDuplicateError = () =>
    toast.error("La empresa ya existe!", { position: "top-center", autoClose: 2000 });

  const { control, handleSubmit, formState, reset } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: { name: empAdmin ? empAdmin.name : "" },
  });

  useEffect(() => {
    reset({ ...empAdmin });
  }, [empAdmin, reset]);

  const { errors } = formState;

  async function onSubmit(data) {
    data.name = data.name.trim().toUpperCase();
    if (!isEdit) {
      data.active = true;
      try {
        const duplicado = await api.post(endpoints_empAdmin.EMPADMIN_NOM, data);
        if (duplicado.length > 0) return notifyDuplicateError();
        dispatch(addEmpAdmin(data)).then(() => {
          notifyAddOk();
          handleClose();
        });
      } catch {
        notifyAddError();
      }
    } else {
      try {
        data.id = empAdmin.id;
        const duplicado = await api.post(endpoints_empAdmin.EMPADMIN_NOM, data);
        if (duplicado.length > 0) return notifyDuplicateError();
        dispatch(updateEmpAdmin(data)).then(() => {
          notifyUpdtateOk();
          handleClose();
        });
      } catch {
        notifyUpdtateError();
      }
    }
  }

  async function handleRemoveEmpAdmin() {
    dispatch(removeEmpAdmin(empAdmin)).then(() => {
      notifyUpdtateOk();
      handleClose();
    });
  }

  return (
    <StyledBox>
      <Typography
        variant={isMobile ? "h6" : "h5"}
        sx={{ fontWeight: "bold", textAlign: "center", mb: 2 }}
      >
        {empAdmin?.name || "Nueva Empresa Administradora"}
      </Typography>
      <Divider sx={{ width: "100%", mb: 3 }} />

      <Controller
        control={control}
        name="name"
        render={({ field }) => (
          <TextField
            {...field}
            label="Nombre"
            placeholder="Nombre empresa"
            fullWidth
            error={!!errors.name}
            helperText={errors?.name?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FuseSvgIcon size={20}>heroicons-solid:user-circle</FuseSvgIcon>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />
        )}
      />

      <ButtonGroup>
        <Button variant="outlined" color="inherit" onClick={handleClose}>
          Cancelar
        </Button>
        <Button variant="contained" color="secondary" onClick={handleSubmit(onSubmit)}>
          Guardar
        </Button>
        {isEdit && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setOpenDelete(true)}
          >
            Eliminar
          </Button>
        )}
      </ButtonGroup>

      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>¿Estás seguro que deseas eliminar la empresa administradora?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancelar</Button>
          <Button onClick={handleRemoveEmpAdmin} autoFocus color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </StyledBox>
  );
}

export default EmpAdminForm;
